import {
  ComponentPropsWithoutRef,
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  CSSProperties,
  useRef,
} from "react";
import { core } from "@servicetitan/hammer-token";
import cx from "classnames";
import styles from "./Page.module.scss";
import Dialog from "../Dialog";
import { PageContext, PageContextProps } from "./PageContext";
import {
  DrillDownContext,
  DrillDownContextProps,
} from "../DrillDown/internal/DrillDownContext";
import { useDrillDownContextState } from "../DrillDown/internal/useDrillDownContextState";
import drillDownStyles from "../DrillDown/DrillDown.module.scss";
import { useDrillDownContext } from "../DrillDown/internal/useDrillDownContext";

/**
 * Props for the PagePanel component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type PagePanelProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Whether the panel is open
   * @default true
   */
  open?: boolean;
  /**
   * Size of the panel
   * @default "medium"
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether to remove default padding from the panel
   * @default false
   */
  noPadding?: boolean;
  /**
   * Sets the default DrillDown index when used in an uncontrolled way
   */
  defaultDrillDownIndex?: DrillDownContextProps["index"];
  /**
   * Callback to pass context to useDrillDown hook
   */
  onDrillDownContextChange?: (context: DrillDownContextProps) => void;
};

const PagePanelInner = ({
  style,
  open,
  children,
}: {
  style?: CSSProperties;
  open?: boolean;
  children?: React.ReactNode;
}) => {
  const { index } = useDrillDownContext();

  const classNames = cx([styles["panel-inner"]], {
    [styles["drilldown-open"]]: index !== undefined,
  });

  return (
    <>
      <div
        className={classNames}
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
        tabIndex={typeof open !== "undefined" && open ? 0 : -1}
        style={{ ...style, overflowY: index === undefined ? "auto" : "hidden" }}
      >
        <div>{children}</div>
      </div>
    </>
  );
};

/**
 * PagePanel component for creating slide-out panels or dialogs.
 *
 * Features:
 * - Responsive design with dialog fallback on mobile
 * - Multiple size variants (small, medium, large)
 * - Optional padding control
 * - Persistent or controlled open state
 * - Automatic mobile dialog conversion
 * - Keyboard navigation and accessibility support
 * - Smooth animations and transitions
 *
 * @example
 * <Page.Panel open={isOpen} size="medium">
 *   <Text variant="headline" el="h3">Panel Content</Text>
 *   <Button onClick={() => setIsOpen(false)}>Close</Button>
 * </Page.Panel>
 */
export const PagePanel = forwardRef(
  (props: PagePanelProps, ref: Ref<HTMLDivElement>) => {
    const {
      children,
      open,
      size = "medium",
      noPadding,
      style,
      className,
      defaultDrillDownIndex,
      onDrillDownContextChange,
      ...rest
    } = props;
    const [isMobile, setIsMobile] = useState<boolean>(false);

    const classNames = cx(
      [styles.panel, drillDownStyles.panel],
      [styles[size]],
      className,
      {
        [styles["open"]]: typeof open === "undefined" ? true : open,
        [styles["persistent"]]: typeof open === "undefined",
        [styles["noPadding"]]: noPadding,
      },
    );

    const drillDownContextValue = useDrillDownContextState({
      defaultDrillDownIndex,
    });

    const drillDownContextProviderValue = useMemo(
      () => ({
        ...drillDownContextValue,
        hostIsInteractive: open !== false,
      }),
      [drillDownContextValue, open],
    );

    useLayoutEffect(() => {
      onDrillDownContextChange?.(drillDownContextProviderValue);
    }, [drillDownContextProviderValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const { pageWidth } = useContext<PageContextProps>(PageContext);
    const previousOpenRef = useRef(open);

    useEffect(() => {
      if (isMobile) {
        previousOpenRef.current = open;
        return;
      }

      if (previousOpenRef.current && open === false) {
        drillDownContextValue.onHostClose?.();
        drillDownContextValue.onHostClosed?.();
      }

      previousOpenRef.current = open;
    }, [open, isMobile, drillDownContextValue]);

    useLayoutEffect(() => {
      if (isMobile || !open || previousOpenRef.current) {
        return;
      }

      drillDownContextValue.setHostTrigger?.(
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null,
      );
    }, [open, isMobile, drillDownContextValue]);

    useLayoutEffect(() => {
      const breakpointWidth = +(
        core.primitive?.BreakpointLg?.value.replace("px", "") ?? 0
      );
      const innerWidth = pageWidth ?? 0;
      setIsMobile(innerWidth < breakpointWidth);
    }, [pageWidth]);

    if (typeof open !== "undefined" && isMobile) {
      return (
        <Dialog
          open={open}
          defaultDrillDownIndex={defaultDrillDownIndex}
          onDrillDownContextChange={onDrillDownContextChange}
        >
          <Dialog.Content>
            <div
              className={classNames}
              aria-hidden={!open}
              aria-disabled={!open}
              ref={ref}
              {...rest}
            >
              {children}
            </div>
          </Dialog.Content>
        </Dialog>
      );
    }
    return (
      <DrillDownContext.Provider value={drillDownContextProviderValue}>
        <div
          className={classNames}
          aria-hidden={!open}
          aria-disabled={!open}
          ref={ref}
          {...rest}
        >
          <PagePanelInner style={style} open={open}>
            {children}
          </PagePanelInner>
        </div>
      </DrillDownContext.Provider>
    );
  },
);

PagePanel.displayName = "PagePanel";
